import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getTelstraConfig,
  getTelstraProducts,
  getTelstraSeedSkus,
  saveTelstraSeedSkus,
  uploadTelstraProducts,
  importTelstraProducts,
  deleteTelstraProducts,
  parseTelstraExcel,
  syncTrackedSkusPriceAndStock,
  getTelstraSyncHistory,
  TelstraDistributorConfig,
  TelstraDistributorProduct,
  TelstraSeedSku,
  TelstraImportOptions,
  TelstraSyncHistoryEntry
} from '../../services/telstraDistributorService';
import { TelstraProductsTable } from './TelstraProductsTable';

interface StatusBadgeProps {
  status: 'ready' | 'no_data' | 'uploading' | 'error';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const styles = {
    ready: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    no_data: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    uploading: 'bg-amber-100 text-amber-700 border-amber-200',
    error: 'bg-rose-100 text-rose-700 border-rose-200'
  };

  const labels = {
    ready: 'Data Loaded',
    no_data: 'No Data',
    uploading: 'Uploading...',
    error: 'Error'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

interface TelstraDistributorTabProps {
  onCatalogueChanged?: () => void;
}

export function TelstraDistributorTab({ onCatalogueChanged }: TelstraDistributorTabProps) {
  const [config, setConfig] = useState<TelstraDistributorConfig | null>(null);
  const [products, setProducts] = useState<TelstraDistributorProduct[]>([]);
  const [seedSkus, setSeedSkus] = useState<TelstraSeedSku[]>([]);
  const [syncHistory, setSyncHistory] = useState<TelstraSyncHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [configData, productsData, skusData, historyData] = await Promise.all([
        getTelstraConfig().catch(() => ({ lastUploadAt: null, lastUploadFileName: null, productCount: null })),
        getTelstraProducts().catch(() => ({ uploadedAt: null, products: [] })),
        getTelstraSeedSkus().catch(() => ({ createdAt: '', updatedAt: null, skus: [] })),
        getTelstraSyncHistory().catch(() => ({ createdAt: '', updatedAt: null, entries: [] }))
      ]);
      setConfig(configData);
      setProducts(productsData.products || []);
      setSeedSkus(skusData.skus || []);
      setSyncHistory(historyData.entries || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load Telstra data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clear success message after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleFileUpload = async (file: File) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls'
    ];
    const isValid = validTypes.some(type => 
      file.type === type || file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')
    );
    
    if (!isValid) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      // Parse Excel file client-side
      const parsedProducts = await parseTelstraExcel(file);
      
      if (parsedProducts.length === 0) {
        setError('No products found in spreadsheet. Please check the file format.');
        return;
      }

      // Upload to backend
      const result = await uploadTelstraProducts(parsedProducts, file.name);
      
      if (result.success) {
        setSuccess(`Successfully uploaded ${result.productCount} products from ${file.name}`);
        await loadData();
      } else {
        setError('Upload failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload spreadsheet');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleSaveSkus = async (updatedSkus: TelstraSeedSku[]) => {
    try {
      await saveTelstraSeedSkus(updatedSkus);
      setSeedSkus(updatedSkus);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to save SKUs');
      return false;
    }
  };

  const handleImportProducts = async (
    productsToImport: TelstraDistributorProduct[],
    options: TelstraImportOptions
  ) => {
    try {
      const result = await importTelstraProducts(productsToImport, options);
      if (result.success) {
        onCatalogueChanged?.();
        const messages = [];
        if (result.importedCount > 0) messages.push(`${result.importedCount} new`);
        if (result.updatedCount > 0) messages.push(`${result.updatedCount} updated`);
        const countText = messages.length > 0 ? messages.join(', ') : 'No new';
        setSuccess(`Successfully imported ${countText} products to your catalogue`);
        return true;
      } else {
        setError(result.error || 'Import failed');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import products');
      return false;
    }
  };

  const handleDeleteSkusAndProducts = async (skusToDelete: string[]) => {
    try {
      // Remove from tracked list
      const updatedSkus = seedSkus.filter(s => !skusToDelete.includes(s.sku));
      await saveTelstraSeedSkus(updatedSkus);
      setSeedSkus(updatedSkus);
      
      // Delete from backend cache
      const productsToDelete = skusToDelete.filter(sku => products.some(p => p.sku === sku));
      if (productsToDelete.length > 0) {
        await deleteTelstraProducts(productsToDelete);
      }
      
      // Update local state
      const remainingProducts = products.filter(p => !skusToDelete.includes(p.sku));
      setProducts(remainingProducts);
      
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to delete products');
      return false;
    }
  };

  /**
   * Sync only price (RRP) and stock (SOH) for all enabled tracked SKUs.
   * This preserves manually entered data like name, brand, image, vendor, etc.
   */
  const handleSyncPriceAndStock = async () => {
    if (products.length === 0) {
      setError('Please upload a Telstra spreadsheet first');
      return;
    }
    
    const enabledSkus = seedSkus.filter(s => s.enabled);
    if (enabledSkus.length === 0) {
      setError('No tracked SKUs to sync. Add some SKUs first.');
      return;
    }

    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);

      const result = await syncTrackedSkusPriceAndStock(seedSkus, products, config?.lastUploadFileName || undefined);
      
      if (result.success) {
        onCatalogueChanged?.();
        const messages = [];
        if (result.importedCount > 0) messages.push(`${result.importedCount} new`);
        if (result.updatedCount > 0) messages.push(`${result.updatedCount} updated`);
        const countText = messages.length > 0 ? messages.join(', ') : 'No changes';
        
        // Show change details
        const changeDetails = [];
        if (result.priceChanges && result.priceChanges.length > 0) {
          changeDetails.push(`${result.priceChanges.length} price change${result.priceChanges.length !== 1 ? 's' : ''}`);
        }
        if (result.stockChanges && result.stockChanges.length > 0) {
          changeDetails.push(`${result.stockChanges.length} stock change${result.stockChanges.length !== 1 ? 's' : ''}`);
        }
        
        const changeText = changeDetails.length > 0 ? ` (${changeDetails.join(', ')})` : '';
        setSuccess(`Price & Stock sync complete: ${countText}${changeText}`);
        
        // Reload data to get updated sync history
        await loadData();
        
        // Auto-expand sync history to show the latest changes
        setShowSyncHistory(true);
      } else {
        setError(result.error || 'Sync failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync price and stock');
    } finally {
      setSyncing(false);
    }
  };

  const getStatus = (): 'ready' | 'no_data' | 'uploading' | 'error' => {
    if (uploading) return 'uploading';
    if (error) return 'error';
    if (!config?.lastUploadAt && products.length === 0) return 'no_data';
    return 'ready';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading Telstra data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-rose-800">Error</h4>
            <p className="text-sm text-rose-700 mt-1">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-rose-600 hover:text-rose-800" title="Dismiss error" aria-label="Dismiss error">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-emerald-800">Success</h4>
            <p className="text-sm text-emerald-700 mt-1">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="text-emerald-600 hover:text-emerald-800" title="Dismiss" aria-label="Dismiss">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Telstra Distributor</h2>
                <p className="text-sm text-neutral-500">Excel spreadsheet upload for Telstra handsets</p>
              </div>
            </div>
            <StatusBadge status={getStatus()} />
          </div>

          {/* Upload Zone */}
          <div className="mt-6">
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-neutral-300 hover:border-neutral-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
                aria-label="Upload Excel file"
              />
              <div className="flex flex-col items-center gap-3">
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    <p className="text-sm text-neutral-600">Processing spreadsheet...</p>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-700">
                        Drop your Telstra Excel spreadsheet here
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">
                        or click to browse (.xlsx, .xls)
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-neutral-50 rounded-lg p-4">
              <p className="text-sm text-neutral-500">Last Upload</p>
              <p className="text-lg font-semibold text-neutral-900 mt-1">
                {formatDate(config?.lastUploadAt || null)}
              </p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <p className="text-sm text-neutral-500">Products Loaded</p>
              <p className="text-lg font-semibold text-neutral-900 mt-1">
                {config?.productCount ?? products.length ?? 0}
              </p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <p className="text-sm text-neutral-500">File</p>
              <p className="text-lg font-semibold text-neutral-900 mt-1 truncate" title={config?.lastUploadFileName || 'None'}>
                {config?.lastUploadFileName || 'None'}
              </p>
            </div>
          </div>

          {/* Sync Price & Stock Action */}
          {seedSkus.length > 0 && products.length > 0 && (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-800">Update Tracked SKUs</h3>
                    <p className="text-xs text-emerald-700">
                      Sync price (RRP) and stock (SOH) for {seedSkus.filter(s => s.enabled).length} enabled SKUs.
                      <br />
                      <strong>Only price & stock will update</strong> — your customizations are preserved.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSyncPriceAndStock}
                  disabled={syncing || uploading}
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {syncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Price & Stock
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-sky-800">About Telstra Distributor</h4>
                  <p className="text-sm text-sky-700 mt-1">
                    Upload Telstra's Excel price list to sync Telstra handset products.
                    Expected columns: <strong>UPOS</strong> (Col B - SKU), <strong>Item Description</strong> (Col D - Name), 
                    <strong>SOH Range</strong> (Col E - Stock), <strong>RRP</strong> (Col J - Price inc GST).
                    Products are sold at the RRP from the spreadsheet and this price cannot be changed.
                  </p>
                  <p className="text-sm text-sky-700 mt-2">
                    <strong>Note:</strong> Re-uploading will update prices and stock only. 
                    Product names you've customized will be preserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <TelstraProductsTable
        seedSkus={seedSkus}
        products={products}
        onSaveSkus={handleSaveSkus}
        onDeleteSkusAndProducts={handleDeleteSkusAndProducts}
        onImportProducts={handleImportProducts}
        onError={setError}
        onSuccess={setSuccess}
      />

      {/* Sync History Section */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => setShowSyncHistory(!showSyncHistory)}
          className="w-full p-6 flex items-center justify-between hover:bg-neutral-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-neutral-900">Sync History</h2>
              <p className="text-sm text-neutral-500">View price and stock changes from syncs</p>
            </div>
          </div>
          <svg 
            className={`w-5 h-5 text-neutral-400 transition-transform ${showSyncHistory ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showSyncHistory && (
          <div className="border-t border-neutral-200">
            {syncHistory.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">No sync history yet.</p>
                <p className="text-xs text-neutral-400 mt-1">
                  Sync history will appear here after you use "Sync Price & Stock".
                </p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200 max-h-[500px] overflow-y-auto">
                {syncHistory.map((entry) => (
                  <div key={entry.id} className="hover:bg-neutral-50">
                    <button
                      onClick={() => setExpandedHistoryId(expandedHistoryId === entry.id ? null : entry.id)}
                      className="w-full p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full ${entry.success ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-neutral-900">
                              {new Date(entry.syncedAt).toLocaleString()}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                              Manual
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                            <span>{entry.productsMatched} matched, {entry.productsUpdated} synced</span>
                            {entry.priceChanges.length > 0 && (
                              <span className="text-amber-600 font-medium">
                                {entry.priceChanges.length} price change{entry.priceChanges.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {entry.stockChanges.length > 0 && (
                              <span className="text-blue-600 font-medium">
                                {entry.stockChanges.length} stock change{entry.stockChanges.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {entry.fileName && (
                            <div className="text-xs text-neutral-400 mt-1">
                              Source: {entry.fileName}
                            </div>
                          )}
                        </div>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-neutral-400 transition-transform ${expandedHistoryId === entry.id ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {expandedHistoryId === entry.id && (
                      <div className="px-4 pb-4 space-y-4">
                        {/* Price Changes */}
                        {entry.priceChanges.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <h4 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Price Changes (RRP inc GST)
                            </h4>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {entry.priceChanges.map((change, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs bg-white rounded p-2">
                                  <div className="flex-1 min-w-0">
                                    <span className="font-mono text-neutral-600">{change.sku}</span>
                                    <span className="mx-2 text-neutral-400">-</span>
                                    <span className="text-neutral-700 truncate">{change.productName}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-right ml-4">
                                    <span className="text-neutral-400 line-through">${change.oldPrice.toFixed(2)}</span>
                                    <span>→</span>
                                    <span className={change.newPrice > change.oldPrice ? 'text-rose-600 font-medium' : 'text-emerald-600 font-medium'}>
                                      ${change.newPrice.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Stock Changes */}
                        {entry.stockChanges.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              Stock on Hand Changes
                            </h4>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {entry.stockChanges.map((change, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs bg-white rounded p-2">
                                  <div className="flex-1 min-w-0">
                                    <span className="font-mono text-neutral-600">{change.sku}</span>
                                    <span className="mx-2 text-neutral-400">-</span>
                                    <span className="text-neutral-700 truncate">{change.productName}</span>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <span className="text-neutral-400">{change.oldSoh ?? 0}</span>
                                    <span>→</span>
                                    <span className={`font-medium ${
                                      (change.newSoh ?? 0) > (change.oldSoh ?? 0) 
                                        ? 'text-emerald-600' 
                                        : (change.newSoh ?? 0) < (change.oldSoh ?? 0)
                                          ? 'text-rose-600'
                                          : 'text-neutral-600'
                                    }`}>
                                      {change.newSoh ?? 0}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* No changes */}
                        {entry.priceChanges.length === 0 && entry.stockChanges.length === 0 && (
                          <div className="text-center py-4 text-sm text-neutral-500">
                            No price or stock changes in this sync.
                          </div>
                        )}
                        
                        {/* Error message if any */}
                        {entry.error && (
                          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">
                            <strong>Error:</strong> {entry.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TelstraDistributorTab;
